/*
  Reverse Node Mapping Utilities

  This module handles conversion from document nodes back to API format,
  including config cleanup and null value removal for backend saving.
*/

import type {
  ApiNode,
  Node as DocNode,
  OutcomeConfig,
  ActionConfig,
  ReviewConfig,
  ReconciliationWorkflowConfig,
  WorkflowConfig,
  RetryConfig,
  OutcomeRef,
} from '../../types';

/**
 * Helper to add a field to config only if it's not null
 */
function addIfNotNull(
  config: Record<string, unknown>,
  originalConfig: Record<string, unknown>,
  fieldName: string,
): void {
  if (originalConfig[fieldName] !== null && originalConfig[fieldName] !== undefined) {
    config[fieldName] = originalConfig[fieldName];
  }
}

/**
 * Helper to add a field to config only if it's defined (not undefined)
 */
function addIfDefined(
  config: Record<string, unknown>,
  originalConfig: Record<string, unknown>,
  fieldName: string,
): void {
  if (originalConfig[fieldName] !== undefined) {
    config[fieldName] = originalConfig[fieldName];
  }
}

/**
 * Reverse transform config for outcome nodes to API format
 */
function toApiOutcomeConfig(originalConfig: OutcomeConfig): Record<string, unknown> {
  const apiConfig: Record<string, unknown> = {
    outcome: originalConfig.outcome,
  };
  // Include enriched fields only if not null
  addIfNotNull(apiConfig, originalConfig, 'customer_outcome_id');
  addIfNotNull(apiConfig, originalConfig, 'outcome_icon');
  addIfNotNull(apiConfig, originalConfig, 'outcome_color');
  addIfNotNull(apiConfig, originalConfig, 'terminal');
  addIfNotNull(apiConfig, originalConfig, 'reconciliation');
  return apiConfig;
}

/**
 * Reverse transform config for action nodes to API format
 */
function toApiActionConfig(
  originalConfig: ActionConfig,
  nodeOutcomes?: OutcomeRef[],
): Record<string, unknown> {
  const apiConfig: Record<string, unknown> = {
    name: originalConfig.action_name,
    type: originalConfig.action_type,
    journey_action_id: originalConfig.action_id,
    journey_action_version_id: originalConfig.action_version_id,
  };
  // Include optional fields only if not null
  addIfNotNull(apiConfig, originalConfig, 'action_version');
  addIfNotNull(apiConfig, originalConfig, 'event_type_title');
  // For action nodes, outcomes are stored in config as array of strings
  if (nodeOutcomes && nodeOutcomes.length > 0) {
    apiConfig.outcomes = nodeOutcomes.map(o => o.label);
  }
  return apiConfig;
}

/**
 * Reverse transform config for review nodes to API format
 */
function toApiReviewConfig(originalConfig: ReviewConfig): Record<string, unknown> {
  const apiConfig: Record<string, unknown> = {};
  if (originalConfig.action_id != null) apiConfig.journey_action_id = originalConfig.action_id;
  if (originalConfig.action_version_id != null) {
    apiConfig.journey_action_version_id = originalConfig.action_version_id;
  }
  // Include review fields only if not null
  addIfNotNull(apiConfig, originalConfig, 'reconciliation');
  addIfNotNull(apiConfig, originalConfig, 'terminal');
  addIfNotNull(apiConfig, originalConfig, 'action_id');
  addIfNotNull(apiConfig, originalConfig, 'action_version_id');
  addIfNotNull(apiConfig, originalConfig, 'action_name');
  addIfNotNull(apiConfig, originalConfig, 'action_type');
  addIfNotNull(apiConfig, originalConfig, 'action_version');
  // Add journey action fields for backend compatibility
  return apiConfig;
}

/**
 * Reverse transform config for reconciliation workflow nodes to API format
 */
function toApiReconciliationWorkflowConfig(
  originalConfig: ReconciliationWorkflowConfig,
  name: string,
): Record<string, unknown> {
  const apiConfig: Record<string, unknown> = {
    name,
    workflow_token: originalConfig.workflow_token,
    workflow_type: originalConfig.workflow_type,
    application_token: originalConfig.workflow_token,
    application_version_id: originalConfig.workflow_version,
    workflow_version_id: originalConfig.workflow_version,
    workflow_current_version: !originalConfig.is_workflow_version_active
      ? null : originalConfig.workflow_version,
  } as Record<string, unknown>;
  // Include reconciliation workflow fields only if not null
  addIfNotNull(apiConfig, originalConfig, 'terminal');
  addIfDefined(apiConfig, originalConfig, 'reconciliation_conditions');

  return apiConfig;
}

/**
 * Reverse transform config for workflow and start_router nodes to API format
 */
function toApiWorkflowConfig(
  originalConfig: WorkflowConfig,
  name: string,
): Record<string, unknown> {
  return {
    name,
    workflow_token: originalConfig.workflow_token,
    workflow_type: originalConfig.workflow_type,
    application_token: originalConfig.workflow_token,
    application_version_id: originalConfig.workflow_version,
    workflow_version_id: originalConfig.workflow_version,
    workflow_current_version: !originalConfig.is_workflow_version_active
      ? null : originalConfig.workflow_version,
  } as Record<string, unknown>;
}

/**
 * Reverse transform config for retry nodes to API format
 */
function toApiRetryConfig(originalConfig: RetryConfig): Record<string, unknown> {
  const apiConfig: Record<string, unknown> = {};
  // Include retry fields only if not null
  addIfNotNull(apiConfig, originalConfig, 'retry_limit');
  return apiConfig;
}

/**
 * Reverse transform config for generic node types to API format
 */
function toApiGenericConfig(originalConfig: Record<string, unknown>): Record<string, unknown> {
  const apiConfig: Record<string, unknown> = {};
  // For other node types, copy all non-null values
  Object.keys(originalConfig).forEach(key => {
    if (originalConfig[key] !== null) {
      apiConfig[key] = originalConfig[key];
    }
  });
  return apiConfig;
}

/**
 * Reverse transform node config to API format based on node type
 */
function toApiNodeConfig(node: DocNode, nodeOutcomes?: OutcomeRef[]): Record<string, unknown> {
  if (!node) return {};
  const { type, config, name } = node;

  // Use type narrowing to ensure the correct config type is passed to each function
  switch (type) {
    case 'outcome':
      return toApiOutcomeConfig(config);
    case 'action':
      return toApiActionConfig(config, nodeOutcomes);
    case 'review':
      return toApiReviewConfig(config);
    case 'reconciliation_workflow':
      return toApiReconciliationWorkflowConfig(config, name ?? '');
    case 'workflow':
    case 'start_router':
    case 'source_workflow':
      return toApiWorkflowConfig(config, name ?? '');
    case 'retry':
      return toApiRetryConfig(config);
    case 'addNodeForm':
    case 'start':
    case 'resume':
      return toApiGenericConfig((config ?? {}) as Record<string, unknown>);
    default:
      return toApiGenericConfig((config ?? {}) as Record<string, unknown>);
  }
}

/**
 * Converts a document model node to save-ready API format with null cleanup
 *
 * This is specifically for backend saving endpoints, which expect null values to be omitted.
 * It also handles transformation of workflow nodes with legacy_step_up_node back to step_up.
 *
 * @param node - The document node to convert
 * @returns The API node ready for backend saving
 */
export function mapDocNodeToSaveApiNode(node: DocNode): ApiNode {
  // Transform outcomes for API format
  // For action nodes, outcomes go into config, not at node level
  const outcomes = (node.outcomes || []).map(o => {
    const outcome: any = { label: o.label };
    if (o.customer_outcome_id !== undefined) outcome.customer_outcome_id = o.customer_outcome_id;
    // For action nodes, also emit as 'name' field for backend compatibility
    if (node.type === 'action') outcome.name = o.label;
    return outcome;
  });

  // Transform workflow nodes with legacy flags back to their original types
  const isLegacyStepUp = node.type === 'workflow' && node.config && (node.config as any).legacy_step_up_node;
  const isLegacyDocv = node.type === 'workflow' && node.config && (node.config as any).legacy_docv_node;
  const isSourceWorkflow = node.type === 'workflow' && node.config && (node.config as any).is_source_workflow;

  let nodeType: string = node.type;
  if (isLegacyStepUp) {
    nodeType = 'step_up';
  } else if (isLegacyDocv) {
    nodeType = 'docv';
  } else if (isSourceWorkflow) {
    nodeType = 'source_workflow';
  } else if (node.type === 'action' && node.config) {
    // For action nodes, use config.action_type as the API node type
    // (end_user, event_action, custom_review, parent_review, service)
    const actionType = (node.config as any).action_type;
    if (actionType) {
      nodeType = actionType;
    }
  }

  const base: ApiNode = {
    id: node.id,
    type: nodeType,
    name: node.name ?? null,
  };

  // Transform config to API format with only non-null values
  if (node.config) {
    const apiConfig = toApiNodeConfig(node, outcomes);
    base.config = apiConfig;
  }

  // For non-action nodes, outcomes go at node level
  if (outcomes.length && node.type !== 'action') {
    base.outcomes = outcomes as any;
  }
  return base;
}

